"use client";

import { useGoogleLogin } from "@react-oauth/google";
import { useLocation } from "react-router-dom";

import { authWithGoogle, pushAnalyticsError, pushDataLayerEvent } from "@/helpers";
import { useGoogleAuth } from "@/hooks";
import { setGoogleAuthLoading } from "@/stores";

type UseGoogleSignInButtonReturn = {
  loading: boolean;
  handleFormSubmit: () => void;
};

/**
 * useGoogleSignInButton Hook
 *
 * Starts Google OAuth and records login versus sign-up in the data layer.
 */
const useGoogleSignInButton = (): UseGoogleSignInButtonReturn => {
  const { pathname } = useLocation();

  const googleAuthState = useGoogleAuth();

  const googleLogin = useGoogleLogin({
    onSuccess: async ({ code }) =>
      authWithGoogle(code, Boolean(pathname?.includes("/sign-up"))),
    onError: () => {
      const isSignUp = Boolean(pathname?.includes("/sign-up"));
      pushAnalyticsError({
        event: isSignUp ? "signup_error" : "login_error",
        errorCode: "oauth_error",
        errorMessage: "Google sign-in failed",
        errorStep: "oauth",
        method: "google",
      });
      setGoogleAuthLoading(false);
    },
    flow: "auth-code",
  });

  const handleFormSubmit = () => {
    const isSignUp = pathname?.includes("/sign-up");

    if (isSignUp) {
      pushDataLayerEvent({
        event: "signup_start",
        method: "google",
      });
    } else {
      pushDataLayerEvent({
        event: "login_start",
        method: "google",
      });
    }

    setGoogleAuthLoading(true);
    googleLogin();
  };

  return {
    loading: googleAuthState.loading,
    handleFormSubmit,
  };
};

export { useGoogleSignInButton };
